import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import RadioGroup from '@atlaskit/radio/radio-group';
import TextArea from '@atlaskit/textarea/text-area';

import { OTHER_ID } from '../../../../../../../../common/types/form/form-fields/field-values';
import { asOptionsValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import {
	asFormOptionField,
	toRadioOptions,
} from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import {
	useCurrentValue,
	useFieldInvalid,
	useFieldRequired,
	useFormField,
	useFormRendererActions,
	useFormRendererState,
} from '../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

const placeholderStyles = xcss({ margin: 'space.500' });

const optionStyles = xcss({ marginTop: 'space.050' });

export const EditRadioInput = ({
	fieldId,
	fieldInputId,
	fieldLabelId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue, useFormField, useFormRendererActions, useFormRendererState);
	const intl = useIntl();

	const isRequired = useFieldRequired({ fieldId });
	const currentValue = asOptionsValue(useCurrentValue({ fieldId }));
	const formField = useFormField({ fieldId });
	const formOptionField = asFormOptionField(formField);
	const { updateModifiedValue } = useFormRendererActions();
	const isInvalid = useFieldInvalid({ fieldId });
	const [otherTextInputValue, setOtherTextInputValue] = useState(currentValue?.otherOptionText);
	useEffect(() => {
		setOtherTextInputValue(currentValue?.otherOptionText);
	}, [currentValue?.otherOptionText, setOtherTextInputValue]);

	const onChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			const selectedOptionId = event.target.value;
			if (selectedOptionId.length === 0 || formOptionField?.options === undefined) {
				updateModifiedValue(fieldId, null);
			} else if (selectedOptionId === OTHER_ID) {
				updateModifiedValue(fieldId, { otherOptionText: '', options: [] });
			} else {
				updateModifiedValue(fieldId, {
					options: formOptionField.options.filter((option) => option.id === selectedOptionId),
				});
			}
		},
		[fieldId, formOptionField?.options, updateModifiedValue],
	);

	const debouncedUpdateModifiedValue = useMemo(
		() => debounce(updateModifiedValue, 500),
		[updateModifiedValue],
	);

	const onOtherOptionTextChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>): void => {
			setOtherTextInputValue(event.target.value);
			debouncedUpdateModifiedValue(fieldId, { otherOptionText: event.target.value, options: [] });
		},
		[debouncedUpdateModifiedValue, fieldId, setOtherTextInputValue],
	);

	if (formOptionField === undefined) {
		return <></>;
	}

	const otherOption = { id: OTHER_ID, label: intl.formatMessage(messages.otherOption) };

	return (
		<Box testId="field-input-radio-edit">
			{formOptionField.hasOtherOption || formOptionField.options.length ? (
				<>
					<RadioGroup
						id={fieldInputId}
						key={fieldInputId}
						name={fieldInputId}
						labelId={fieldLabelId}
						options={
							formOptionField.hasOtherOption
								? toRadioOptions([...formOptionField.options, otherOption])
								: toRadioOptions(formOptionField.options)
						}
						value={
							currentValue?.otherOptionText !== undefined
								? OTHER_ID
								: currentValue?.options.length
									? currentValue?.options[0].id
									: null
						}
						onChange={onChange}
						testId={`field-input-radio-edit-${fieldId}`}
						isRequired={isRequired}
						isInvalid={isInvalid}
						aria-describedby={
							isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
						}
					/>
					{otherTextInputValue !== undefined && (
						<Box xcss={optionStyles}>
							<TextArea
								appearance="standard"
								placeholder={intl.formatMessage(messages.otherOption)}
								minimumRows={2}
								isMonospaced={false}
								value={otherTextInputValue}
								resize="smart"
								spellCheck={false}
								onChange={onOtherOptionTextChange}
								testId={`field-input-radio-edit-other-${fieldId}`}
								aria-label={intl.formatMessage(messages.otherOptionTextLabel, {
									label: formField.label,
								})}
								aria-describedby={
									isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
								}
							/>
						</Box>
					)}
				</>
			) : (
				<Box xcss={placeholderStyles} />
			)}
		</Box>
	);
};
